import { ScenarioData } from '@inbox-demo-nx/types';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useScenarioContext } from './scenario';

if (typeof Intl === 'undefined') {
  require('intl');
  require('intl/locale-data/jsonp/en');
  require('@formatjs/intl-locale/polyfill');
  require('@formatjs/intl-getcanonicallocales/polyfill');
  require('@formatjs/intl-pluralrules/polyfill');
  require('@formatjs/intl-pluralrules/locale-data/en'); // locale-data for en
  require('@formatjs/intl-relativetimeformat/polyfill');
  require('@formatjs/intl-relativetimeformat/locale-data/en');
}

const getIntlObjects = (scenario: ScenarioData) => {
  const intlTimeFormat = Intl.DateTimeFormat(scenario.defaultLanguage, {
    hour: 'numeric',
    minute: 'numeric',
  });

  const intlDateFormat = Intl.DateTimeFormat(scenario.defaultLanguage, {
    month: 'short',
    day: 'numeric',
  });

  const intlDateDifferentYearFormat = Intl.DateTimeFormat(
    scenario.defaultLanguage,
    {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }
  );

  const intlDateTimeFormat = Intl.DateTimeFormat(scenario.defaultLanguage, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });

  const intlDateLineLastYearFormat = Intl.DateTimeFormat(
    scenario.defaultLanguage,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }
  );

  const intlDateLineFormat = Intl.DateTimeFormat(scenario.defaultLanguage, {
    month: 'short',
    day: 'numeric',
    weekday: 'long',
  });

  const intlDateLineRelativeFormat = new Intl.RelativeTimeFormat(
    scenario.defaultLanguage,
    { numeric: 'auto' }
  );

  return {
    intlTimeFormat,
    intlDateFormat,
    intlDateDifferentYearFormat,
    intlDateTimeFormat,
    intlDateLineLastYearFormat,
    intlDateLineFormat,
    intlDateLineRelativeFormat,
  };
};

export const IntlContext = createContext<ReturnType<typeof getIntlObjects>>(
  undefined as any
);

export const useIntlContext = () => useContext(IntlContext);

export const IntlContextProvider = ({ children }: { children: ReactNode }) => {
  const scenario = useScenarioContext();

  const contextValue = useMemo(() => getIntlObjects(scenario), [scenario]);

  return (
    <IntlContext.Provider value={contextValue}>{children}</IntlContext.Provider>
  );
};
